import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { submitContact } from "@/lib/api";
import { categories } from "@/lib/siteConfig";
import { commonContent } from "@/cms/cmsModel";
import { useCmsDocument } from "@/cms/CmsContentProvider";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ContactForm = ({
  sourcePage = "unbekannt",
  defaultCategory = "allgemeine-anfrage",
  showCategory = true,
  showPhone = true,
  testidPrefix = "contact-form",
  submitLabel = "Anfrage senden",
  contextLabel,
}) => {
  const copy = commonContent.form;
  const { locale } = useCmsDocument();
  // `website` is a honeypot: hidden from humans, but bots tend to fill every
  // field. If it ever arrives non-empty, the backend silently drops the request.
  const empty = { name: "", email: "", phone: "", category: defaultCategory, message: "", website: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = copy.nameError;
    if (!form.email || !emailRe.test(form.email.trim()))
      e.email = copy.emailError;
    if (!form.message || form.message.trim().length < 5)
      e.message = copy.messageError;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const categoryLabel =
        categories.find((c) => c.value === form.category)?.label || form.category;
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: `${categoryLabel} (${sourcePage})`,
        message: form.message.trim(),
        website: form.website, // honeypot — humans leave this empty
      });
      setDone(true);
      setForm({ ...empty, category: defaultCategory });
      toast.success(locale === "ar" ? "شكراً! تم إرسال استفساركم." : "Vielen Dank! Ihre Anfrage wurde gesendet.", {
        description: locale === "ar" ? "سنتواصل معكم بأسرع وقت." : "Wir melden uns schnellstmöglich bei Ihnen.",
      });
    } catch (err) {
      // Show the server's message when it provides one (e.g. rate-limit notice).
      const serverMsg = err?.response?.data?.error;
      toast.error(serverMsg || (locale === "ar" ? "حدث خطأ." : "Es ist ein Fehler aufgetreten."), {
        description: serverMsg
          ? undefined
          : locale === "ar" ? "يرجى المحاولة مرة أخرى أو الاتصال بنا." : "Bitte versuchen Sie es erneut oder rufen Sie uns an.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-white rounded-xl border-[#CBD6E2] text-[#193D6D] placeholder:text-[#8A94A0] focus-visible:ring-2 focus-visible:ring-[#7A1516]/25 focus-visible:border-[#7A1516]/40";
  const labelCls = "mb-1.5 block text-sm font-semibold text-[#26303c]";
  const errCls = "mt-1 text-xs text-[#9b2c2c] az-anim-error";

  if (done) {
    return (
      <div
        className="az-anim-pop flex flex-col items-center justify-center rounded-2xl border border-[#cfe3d6] bg-[#f3f8f4] px-6 py-12 text-center"
        data-testid={`${testidPrefix}-success-text`}
      >
        <CheckCircle2 size={44} className="text-[#2f6f55]" />
        <h3 className="mt-4 font-heading text-2xl font-semibold text-[#193D6D]">
          {copy.successTitle}
        </h3>
        <p className="mt-2 max-w-md text-[#586576]">
          {copy.successText}
        </p>
        <Button
          onClick={() => setDone(false)}
          variant="outline"
          className="mt-6 rounded-xl border-[#7A1516]/40 text-[#7A1516] hover:bg-[#7A1516]/[0.06]"
          data-testid={`${testidPrefix}-reset-button`}
        >
          {copy.resetButton}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid={testidPrefix} noValidate>
      {/* Honeypot: visually hidden and skipped by keyboard/screen readers, so
          only bots fill it. Not a real field for users. */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" >
        <label htmlFor={`${testidPrefix}-website`}>Website (bitte leer lassen)</label>
        <input
          id={`${testidPrefix}-website`}
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => update("website", e.target.value)}
        />
      </div>

      {contextLabel && (
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--svc-primary,#7A1516)]">
          {contextLabel}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor={`${testidPrefix}-name`} className={labelCls}>
            {copy.nameLabel} <span className="text-[var(--svc-primary,#7A1516)]">*</span>
          </Label>
          <Input
            id={`${testidPrefix}-name`}
            data-testid={`${testidPrefix}-name-input`}
            className={inputCls}
            placeholder={copy.namePlaceholder}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            autoComplete="name"
          />
          {errors.name && (
            <p className={errCls} data-testid={`${testidPrefix}-name-error`}>
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor={`${testidPrefix}-email`} className={labelCls}>
            {copy.emailLabel} <span className="text-[var(--svc-primary,#7A1516)]">*</span>
          </Label>
          <Input
            id={`${testidPrefix}-email`}
            type="email"
            data-testid={`${testidPrefix}-email-input`}
            className={inputCls}
            placeholder={copy.emailPlaceholder}
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            autoComplete="email"
          />
          {errors.email && (
            <p className={errCls} data-testid={`${testidPrefix}-email-error`}>
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {showPhone && (
          <div>
            <Label htmlFor={`${testidPrefix}-phone`} className={labelCls}>
              {copy.phoneLabel} <span className="font-normal text-[#8A94A0]">{copy.optionalLabel}</span>
            </Label>
            <Input
              id={`${testidPrefix}-phone`}
              data-testid={`${testidPrefix}-phone-input`}
              className={inputCls}
              placeholder={copy.phonePlaceholder}
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              autoComplete="tel"
            />
          </div>
        )}

        {showCategory && (
          <div>
            <Label htmlFor={`${testidPrefix}-category`} className={labelCls}>
              {copy.categoryLabel}
            </Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger
                id={`${testidPrefix}-category`}
                data-testid={`${testidPrefix}-category-select`}
                className={inputCls}
              >
                <SelectValue placeholder={copy.categoryPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`${testidPrefix}-category-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div>
        <Label htmlFor={`${testidPrefix}-message`} className={labelCls}>
          {copy.messageLabel} <span className="text-[var(--svc-primary,#7A1516)]">*</span>
        </Label>
        <Textarea
          id={`${testidPrefix}-message`}
          data-testid={`${testidPrefix}-message-textarea`}
          className={`${inputCls} min-h-[120px] resize-y`}
          placeholder={copy.messagePlaceholder}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
        />
        {errors.message && (
          <p className={errCls} data-testid={`${testidPrefix}-message-error`}>
            {errors.message}
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid={`${testidPrefix}-submit-button`}
        className="w-full rounded-xl bg-[var(--svc-primary,#7A1516)] py-6 text-base text-[var(--svc-invert,#F1F4F8)] hover:bg-[var(--svc-primary-hover,#941d1e)] disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="mr-2 animate-spin" /> {copy.sendingLabel}
          </>
        ) : (
          <>
            {submitLabel} <Send size={16} className="ml-2" />
          </>
        )}
      </Button>
      <p className="text-center text-xs text-[#8A94A0]">
        {copy.consent}
      </p>
    </form>
  );
};
